import React, { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Trash2, UserPlus, ShieldAlert, AlertTriangle, RotateCcw, Users } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";

export default function AdminTeam() {
    const [bootstrap, setBootstrap] = useState([]);
    const [granted, setGranted] = useState([]);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const fetchAdmins = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admins");
            setBootstrap(data.bootstrap);
            setGranted(data.granted);
        } catch (e) {
            toast.error(formatApiError(e, "Failed to load admins"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmins();
    }, []);

    const handleGrant = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        const trimmed = email.trim().toLowerCase();
        if (!trimmed.endsWith("@therapymgmt.com")) {
            toast.error("Email must be a @therapymgmt.com address");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/admins", { email: trimmed });
            toast.success(`${trimmed} is now an admin`);
            setEmail("");
            fetchAdmins();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleRevoke = async (em) => {
        if (!window.confirm(`Revoke admin access for ${em}?`)) return;
        try {
            await api.delete(`/admins/${encodeURIComponent(em)}`);
            toast.success(`${em} demoted to voter`);
            fetchAdmins();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="App min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1">
                <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-team">
                    <div className="label-overline">Admin · Team</div>
                    <h1 className="font-serif text-4xl text-tmc-ink mt-3">Admin team</h1>
                    <p className="text-tmc-muted mt-2 max-w-2xl">
                        Anyone listed here can curate submissions, mark finalists, and run the voting window. Voters
                        across your tenant can sign in but only see the voting gallery.
                    </p>

                    <form
                        onSubmit={handleGrant}
                        className="mt-10 tmc-card p-6 flex flex-col sm:flex-row gap-3 sm:items-end"
                        data-testid="grant-admin-form"
                    >
                        <div className="flex-1">
                            <label className="label-overline block mb-2">Promote a teammate</label>
                            <input
                                type="email"
                                placeholder="name@therapymgmt.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="tmc-input"
                                data-testid="grant-admin-email"
                                required
                            />
                            <p className="text-xs text-tmc-muted mt-2">
                                They'll become an admin the next time they sign in (or instantly, if they're already signed in).
                            </p>
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="tmc-btn-primary"
                            data-testid="grant-admin-submit"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                            Add admin
                        </button>
                    </form>

                    {/* Bootstrap admins */}
                    <div className="mt-12">
                        <div className="flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4 text-tmc-terracotta" />
                            <h2 className="font-serif text-2xl text-tmc-ink">Bootstrap admins</h2>
                        </div>
                        <p className="text-sm text-tmc-muted mt-1">
                            Set in server config. Cannot be revoked from this screen — change the
                            <code className="mx-1 px-1.5 py-0.5 rounded bg-tmc-sand text-xs">ADMIN_EMAILS</code>
                            env var on the server to remove one.
                        </p>
                        {loading ? (
                            <Skeleton />
                        ) : (
                            <div className="mt-4 tmc-card divide-y divide-tmc-line" data-testid="bootstrap-list">
                                {bootstrap.map((a) => (
                                    <Row key={a.email} admin={a} />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Granted admins */}
                    <div className="mt-12">
                        <div className="flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4 text-tmc-mossDark" />
                            <h2 className="font-serif text-2xl text-tmc-ink">Granted admins</h2>
                        </div>
                        {loading ? (
                            <Skeleton />
                        ) : granted.length === 0 ? (
                            <div className="mt-4 tmc-card p-8 text-center text-tmc-muted">
                                No additional admins yet. Use the form above to add one.
                            </div>
                        ) : (
                            <div className="mt-4 tmc-card divide-y divide-tmc-line" data-testid="granted-list">
                                {granted.map((a) => (
                                    <Row key={a.email} admin={a} onRevoke={() => handleRevoke(a.email)} />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Danger Zone */}
                    <ManagersSection />
                    <DangerZone />
                </section>
            </main>
            <Footer />
        </div>
    );
}

function Row({ admin, onRevoke }) {
    return (
        <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-row-${admin.email}`}>
            <div className="flex-1 min-w-0">
                <div className="font-medium text-tmc-ink truncate">{admin.name || admin.email}</div>
                <div className="text-sm text-tmc-muted truncate">{admin.email}</div>
            </div>
            <div className="flex items-center gap-4">
                <div className="text-xs text-tmc-muted">
                    {admin.last_login ? (
                        <>Last sign-in {new Date(admin.last_login).toLocaleDateString()}</>
                    ) : (
                        <span className="italic">Never signed in</span>
                    )}
                    {admin.granted_by && (
                        <div className="mt-0.5">By {admin.granted_by}</div>
                    )}
                </div>
                {onRevoke && (
                    <button
                        onClick={onRevoke}
                        className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                        title="Revoke admin"
                        data-testid={`revoke-${admin.email}`}
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}

function Skeleton() {
    return (
        <div className="mt-4 flex items-center gap-2 text-tmc-muted">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
    );
}

function ManagersSection() {
    const [managers, setManagers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState("");
    const [adding, setAdding] = useState(false);

    const fetchManagers = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/managers");
            setManagers(data);
        } catch (e) {
            toast.error(formatApiError(e, "Failed to load managers"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchManagers(); }, []);

    const handleAdd = async (e) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) return;
        setAdding(true);
        try {
            await api.post("/managers", { name: trimmed });
            toast.success(`${trimmed} added to manager list`);
            setName("");
            fetchManagers();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setAdding(false);
        }
    };

    const handleRemove = async (m) => {
        if (!window.confirm(`Remove "${m}" from the manager list?`)) return;
        try {
            await api.delete(`/managers/${encodeURIComponent(m)}`);
            toast.success(`${m} removed`);
            fetchManagers();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="mt-16" data-testid="managers-section">
            <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-tmc-water" />
                <h2 className="font-serif text-2xl text-tmc-ink">Managers</h2>
                <span className="text-sm text-tmc-muted ml-2">({managers.length})</span>
            </div>
            <p className="text-sm text-tmc-muted mt-1 max-w-2xl">
                Names that appear in the submission form's manager dropdown. Add or remove without redeploying.
            </p>

            <form
                onSubmit={handleAdd}
                className="mt-5 tmc-card p-5 flex flex-col sm:flex-row gap-3 sm:items-end"
                data-testid="add-manager-form"
            >
                <div className="flex-1">
                    <label className="label-overline block mb-2">Add a manager</label>
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="First Last"
                        className="tmc-input"
                        maxLength={120}
                        data-testid="add-manager-name"
                        required
                    />
                </div>
                <button
                    type="submit"
                    disabled={adding}
                    className="tmc-btn-primary"
                    data-testid="add-manager-submit"
                >
                    {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                    Add
                </button>
            </form>

            {loading ? (
                <Skeleton />
            ) : managers.length === 0 ? (
                <div className="mt-4 tmc-card p-8 text-center text-tmc-muted">
                    No managers yet. Add one above.
                </div>
            ) : (
                <div
                    className="mt-4 tmc-card p-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1"
                    data-testid="managers-list"
                >
                    {managers.map((m) => (
                        <div
                            key={m}
                            className="px-3 py-2 rounded-lg flex items-center justify-between gap-2 hover:bg-tmc-sand/60 transition-colors"
                            data-testid={`manager-row-${m}`}
                        >
                            <span className="text-sm text-tmc-ink truncate" title={m}>{m}</span>
                            <button
                                onClick={() => handleRemove(m)}
                                className="p-1.5 rounded-md text-red-600 hover:bg-red-50 shrink-0"
                                title="Remove"
                                data-testid={`remove-manager-${m}`}
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function DangerZone() {
    const [open, setOpen] = useState(null); // 'votes' | 'finalists' | null
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);

    const close = () => {
        if (busy) return;
        setOpen(null);
        setConfirmText("");
    };

    const handleConfirm = async () => {
        if (confirmText.trim().toUpperCase() !== "RESET") return;
        setBusy(true);
        try {
            if (open === "votes") {
                const { data } = await api.post("/admin/reset-votes");
                toast.success(`Cleared ${data.votes_deleted} vote${data.votes_deleted === 1 ? "" : "s"} · voting closed`);
            } else if (open === "finalists") {
                const { data } = await api.post("/admin/reset-finalists");
                toast.success(`${data.demoted} finalist${data.demoted === 1 ? "" : "s"} demoted to approved`);
            }
            close();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const isVotes = open === "votes";
    const config = isVotes
        ? {
              title: "Reset all votes?",
              desc: "This will permanently delete every vote, close voting, and clear the voting deadline. Finalists stay finalists. This cannot be undone.",
              cta: "Yes, reset votes",
          }
        : {
              title: "Reset finalists?",
              desc: "This will demote every current finalist back to 'approved' so you can re-curate. Existing votes are NOT deleted, but they'll point at non-finalists. Run 'Reset all votes' afterward if you want a clean slate.",
              cta: "Yes, demote finalists",
          };

    return (
        <div className="mt-16">
            <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <h2 className="font-serif text-2xl text-tmc-ink">Danger zone</h2>
            </div>
            <p className="text-sm text-tmc-muted mt-1 max-w-2xl">
                Destructive actions for ending or resetting the event. Both prompt for typed confirmation.
            </p>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="tmc-card p-5 border-red-200" data-testid="danger-reset-votes">
                    <div className="flex items-start gap-3">
                        <RotateCcw className="w-5 h-5 text-red-600 mt-0.5" />
                        <div className="flex-1">
                            <div className="font-medium text-tmc-ink">Reset all votes</div>
                            <p className="text-sm text-tmc-muted mt-1">
                                Deletes every vote, closes voting, and clears the deadline.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => setOpen("votes")}
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-red-300 text-red-700 hover:bg-red-50 transition-colors"
                        data-testid="reset-votes-btn"
                    >
                        Reset votes
                    </button>
                </div>

                <div className="tmc-card p-5 border-red-200" data-testid="danger-reset-finalists">
                    <div className="flex items-start gap-3">
                        <RotateCcw className="w-5 h-5 text-red-600 mt-0.5" />
                        <div className="flex-1">
                            <div className="font-medium text-tmc-ink">Reset finalists</div>
                            <p className="text-sm text-tmc-muted mt-1">
                                Demotes every finalist back to 'approved' so you can re-pick the slate.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => setOpen("finalists")}
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-red-300 text-red-700 hover:bg-red-50 transition-colors"
                        data-testid="reset-finalists-btn"
                    >
                        Reset finalists
                    </button>
                </div>
            </div>

            <Dialog open={open !== null} onOpenChange={(o) => { if (!o) close(); }}>
                <DialogContent className="max-w-md" data-testid="danger-confirm-dialog">
                    <DialogHeader>
                        <div className="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-red-100 mb-2">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <DialogTitle className="text-center font-serif text-2xl text-tmc-ink">
                            {config.title}
                        </DialogTitle>
                        <DialogDescription className="text-center text-tmc-inkSoft">
                            {config.desc}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-2">
                        <label className="block text-xs text-tmc-muted mb-2">
                            Type <strong className="text-red-700">RESET</strong> to confirm
                        </label>
                        <input
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            className="tmc-input"
                            placeholder="RESET"
                            autoFocus
                            data-testid="danger-confirm-input"
                        />
                    </div>
                    <DialogFooter className="sm:justify-between gap-2">
                        <button
                            onClick={close}
                            disabled={busy}
                            className="px-4 py-2 rounded-full text-sm font-medium text-tmc-inkSoft hover:bg-tmc-sand"
                            data-testid="danger-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={busy || confirmText.trim().toUpperCase() !== "RESET"}
                            className="px-5 py-2 rounded-full text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                            data-testid="danger-confirm"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                            {config.cta}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
